/**
 * TipTapEditor - Unified rich text editor component
 * 
 * A rich text editor that replaces all Jodit Pro editors with a single configurable component.
 * Supports:
 * - Rich text formatting
 * - Placeholder autocomplete (${}, !{}, ={})
 * - Custom image upload
 * - Table editing
 * - Source view toggle
 * - Plain text output (stripHtml)
 * - Character limits with warning
 * - Document (A4) variant styling
 */

import React, { useCallback, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { createRoot } from 'react-dom/client';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Link } from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Placeholder } from '@tiptap/extension-placeholder';
import { CharacterCount } from '@tiptap/extension-character-count';
import { Extension } from '@tiptap/core';
import { TextStyle } from '@tiptap/extension-text-style';
import { Alert } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ErrorIcon from '@mui/icons-material/Error';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { createAppTheme } from '../../Theme.js';
import useThemeStore, { resolveMode } from '../../stores/useThemeStore';

import { Markdown } from '@tiptap/markdown';
import { PlaceholderNode, createPlaceholderSuggestion, createPlaceholderSuggestionExtension } from './PlaceholderExtension';
import PlaceholderList from './PlaceholderList';
import EditorToolbar from './EditorToolbar';
import { CustomImageExtension, uploadImage } from './CustomImage';
import { convertPlaceholderTextToNodes } from './placeholderHtmlConversion';
import './TipTapEditor.css';

/**
 * Converts @agent(uuid) tokens in markdown to HTML spans that TipTap can parse
 * into PlaceholderNode chips. Uses the agentMentions list to resolve display names.
 */
const preprocessAgentMentions = (markdown, agentMentions = []) => {
  if (!markdown || typeof markdown !== 'string') return markdown;
  return markdown.replace(/@agent\(([^)]+)\)/g, (match, agentId) => {
    const agent = agentMentions.find(a => a.id === agentId);
    const name = agent ? agent.key : 'Unknown Agent';
    return `<span data-placeholder-key="${name}" data-placeholder-prefix="@" data-placeholder-type="agent" data-placeholder-id="${agentId}">@${name}</span>`;
  });
};

// FontSize extension
const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize || null,
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize }).run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
      },
    };
  },
});

// TextStyle extension imported at top for font size support

/**
 * Creates the suggestion configuration with tippy.js popup
 */
const createSuggestionWithPopup = (triggerChar, getPlaceholders) => {
  let popup = null;
  let component = null;

  return {
    char: triggerChar,
    allowSpaces: false,
    startOfLine: false,

    items: ({ query }) => {
      const placeholders = getPlaceholders(triggerChar);
      if (!placeholders || !Array.isArray(placeholders)) return [];

      return placeholders
        .filter(item => {
          if (triggerChar === '@') {
            return item.key.toLowerCase().includes(query.toLowerCase());
          }
          const label = item.label || '';
          const searchText = `${triggerChar}{${item.key}}`;
          return searchText.toLowerCase().includes(query.toLowerCase()) ||
                 item.key.toLowerCase().includes(query.toLowerCase()) ||
                 label.toLowerCase().includes(query.toLowerCase());
        })
        .slice(0, 10);
    },

    render: () => {
      let listRef = { current: null };
      
      return {
        onStart: props => {
          const container = document.createElement('div');
          container.className = 'placeholder-suggestion-popup';

          // Create React root for the list
          const root = createRoot(container);

          const getPopupTheme = () => {
            const mode = resolveMode(useThemeStore.getState().preference);
            return createAppTheme(mode);
          };

          const updateList = (items, command) => {
            root.render(
              <ThemeProvider theme={getPopupTheme()}>
                <PlaceholderList
                  ref={listRef}
                  items={items}
                  command={command}
                  triggerChar={triggerChar}
                />
              </ThemeProvider>
            );
          };

          updateList(props.items, props.command);

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: container,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            maxWidth: 400,
            zIndex: 99999,
          })[0];

          component = {
            root,
            container,
            updateList,
            listRef,
          };
        },

        onUpdate: props => {
          if (component) {
            component.updateList(props.items, props.command);
          }

          if (popup) {
            popup.setProps({
              getReferenceClientRect: props.clientRect,
            });
          }
        },

        onKeyDown: props => {
          if (props.event.key === 'Escape') {
            popup?.hide();
            return true;
          }

          // Forward keyboard events to PlaceholderList via ref
          if (component?.listRef?.current?.onKeyDown) {
            return component.listRef.current.onKeyDown(props);
          }

          return false;
        },

        onExit: () => {
          if (popup) {
            popup.destroy();
            popup = null;
          }
          if (component) {
            component.root.unmount();
            component = null;
          }
        },
      };
    },

    command: ({ editor, range, props }) => {
      if (popup) {
        popup.destroy();
        popup = null;
      }
      if (component) {
        component.root.unmount();
        component = null;
      }
      
      if (triggerChar === '@' && props.id) {
        editor.chain().focus().deleteRange(range)
          .insertContent({
            type: 'placeholderNode',
            attrs: {
              prefix: '@',
              key: props.key,
              type: props.type || 'agent',
              isValid: true,
              id: props.id,
            },
          })
          .run();
        return;
      }

      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({
          type: 'placeholderNode',
          attrs: {
            prefix: triggerChar,
            key: props.key,
            displayKey: props.label && props.label !== props.key ? props.label : null,
            type: props.type || 'text',
            isValid: true,
            id: props.id || null,
          },
        })
        .run();
    },
  };
};

const TipTapEditor = forwardRef(({
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder = '',
  availablePlaceholders = [],
  agentMentions = [],
  projectId = '',
  readOnly = false,
  showToolbar = true,
  minHeight = 'auto',
  className = '',
  triggerChars = ['$'], // Which trigger characters to enable
  stripHtml = false, // Output plain text instead of HTML
  markdown: markdownMode = false, // Input/output markdown instead of HTML
  characterLimit = null, // Show warning when exceeded (e.g., 1073741823)
  variant = 'default', // 'default' | 'document' (A4 page styling) | 'page' (borderless full-page WYSIWYG)
  error = false, // Error state for validation
  helperText = '', // Helper/error text to display
}, ref) => {
  const [isSourceMode, setIsSourceMode] = useState(false);
  const [sourceContent, setSourceContent] = useState('');
  const [charCount, setCharCount] = useState(0);
  const editorContainerRef = useRef(null);
  // Track the last value prop used to set content (prevents infinite updates)
  const lastValueRef = useRef(value);

  // Use a ref so the getPlaceholders closure always reads the latest value,
  // even though it's captured once by the suggestion extension at editor creation time.
  const availablePlaceholdersRef = useRef(availablePlaceholders);
  useEffect(() => {
    availablePlaceholdersRef.current = availablePlaceholders;
  }, [availablePlaceholders]);

  const agentMentionsRef = useRef(agentMentions);
  useEffect(() => {
    agentMentionsRef.current = agentMentions;
  }, [agentMentions]);

  // Get placeholders for a specific trigger character
  const getPlaceholders = useCallback((triggerChar) => {
    if (triggerChar === '@') {
      return agentMentionsRef.current || [];
    }

    const current = availablePlaceholdersRef.current;
    if (!current || current.length === 0) {
      return [];
    }

    const placeholdersList = current[0]?.placeholders || current;

    if (triggerChar === '!') {
      return placeholdersList.filter(p => p.type === 'form_placeholder');
    } else if (triggerChar === '$') {
      return placeholdersList.filter(p => p.type !== 'form_placeholder');
    } else if (triggerChar === '=') {
      return placeholdersList.filter(p => p.type === 'function' || p.type === 'formula');
    }
    
    return placeholdersList;
  }, []);

  // Create suggestion extensions for each trigger character
  // Each trigger char gets its own extension with a unique name/key
  const suggestionExtensions = triggerChars.map(char => {
    const Extension = createPlaceholderSuggestionExtension(char);
    return Extension.configure({
      suggestion: createSuggestionWithPopup(char, getPlaceholders),
    });
  });

  const initialContent = markdownMode ? preprocessAgentMentions(value, agentMentions) : convertPlaceholderTextToNodes(value, availablePlaceholders);

  const baseExtensions = [
    StarterKit.configure({
      history: true,
      link: false,
      underline: false,
      placeholder: false,
    }),
    PlaceholderNode,
    ...suggestionExtensions,
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableCell.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          backgroundColor: {
            default: null,
            parseHTML: (el) => el.style.backgroundColor || null,
            renderHTML: (attrs) => {
              if (!attrs.backgroundColor) return {};
              return { style: `background-color: ${attrs.backgroundColor}` };
            },
          },
        };
      },
    }),
    TableHeader.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          backgroundColor: {
            default: null,
            parseHTML: (el) => el.style.backgroundColor || null,
            renderHTML: (attrs) => {
              if (!attrs.backgroundColor) return {};
              return { style: `background-color: ${attrs.backgroundColor}` };
            },
          },
        };
      },
    }),
    Link.configure({
      openOnClick: false,
      isAllowedUri: (uri, ctx) => {
        if (uri && /[$!=]\{[^}]+\}/.test(uri)) return true;
        return ctx.defaultValidate(uri);
      },
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Underline,
    Subscript,
    Superscript,
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    FontSize,
    Placeholder.configure({
      placeholder: placeholder,
    }),
    CharacterCount,
    CustomImageExtension.configure({
      projectId: projectId,
      inline: false,
      allowBase64: false,
    }),
  ];

  if (markdownMode) {
    baseExtensions.push(Markdown.configure({ markedOptions: { gfm: true } }));
  }

  const getOutput = (editor) => {
    if (stripHtml) return editor.getText();
    if (markdownMode) return editor.getMarkdown();
    return editor.getHTML();
  };

  const editor = useEditor({
    extensions: baseExtensions,
    content: initialContent,
    ...(markdownMode ? { contentType: 'markdown' } : {}),
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      setCharCount(editor.storage.characterCount?.characters() || 0);
      
      if (onChange && !isSourceMode) {
        onChange(getOutput(editor));
      }
    },
    onBlur: ({ editor, event }) => {
      if (onBlur) {
        onBlur(getOutput(editor), event);
      }
    },
    onFocus: ({ editor, event }) => {
      if (onFocus) {
        onFocus(event);
      }
    },
  });

  // Keep editable state in sync with readOnly prop
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // Re-validate placeholder chips whenever available placeholders change.
  useEffect(() => {
    if (!editor) return;
    const list = availablePlaceholders[0]?.placeholders || availablePlaceholders;
    if (!Array.isArray(list)) return;

    const validKeys = new Set(list.map(p => p.key));

    const { doc, tr } = editor.state;
    let changed = false;

    doc.descendants((node, pos) => {
      if (node.type.name === 'placeholderNode') {
        const { key, prefix, isValid } = node.attrs;
        if (prefix === '@') return;
        const shouldBeValid = validKeys.has(key);
        if (shouldBeValid !== isValid) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, isValid: shouldBeValid });
          changed = true;
        }
      }
    });

    if (changed) {
      editor.view.dispatch(tr);
    }
  }, [editor, availablePlaceholders]);

  // Expose editor methods via ref
  useImperativeHandle(ref, () => ({
    editor,
    getHTML: () => editor?.getHTML() || '',
    getText: () => editor?.getText() || '',
    setContent: (content) => {
      // Convert placeholder text patterns before setting content
      const convertedContent = convertPlaceholderTextToNodes(content, availablePlaceholders);
      editor?.commands.setContent(convertedContent);
    },
    focus: () => editor?.commands.focus(),
    insertPlaceholder: (placeholder) => {
      if (editor && placeholder) {
        const key = placeholder.text || placeholder.key;
        const label = placeholder.label;
        editor.chain().focus().insertContent({
          type: 'placeholderNode',
          attrs: {
            prefix: '$',
            key,
            displayKey: label && label !== key ? label : null,
            type: placeholder.type || 'text',
            isValid: true,
          },
        }).run();
      }
    },
  }));

  // Update content when value prop changes, or re-convert raw ${key} tokens on reopen.
  useEffect(() => {
    if (!editor || isSourceMode) return;

    const applyConvertedContent = () => {
      lastValueRef.current = value;
      if (markdownMode) {
        editor.commands.setContent(preprocessAgentMentions(value, agentMentionsRef.current), { contentType: 'markdown' });
      } else {
        const convertedContent = convertPlaceholderTextToNodes(value, availablePlaceholders);
        editor.commands.setContent(convertedContent, false);
      }
    };

    if (value !== lastValueRef.current) {
      const currentOutput = getOutput(editor);
      if (currentOutput === value) {
        lastValueRef.current = value;
      } else {
        applyConvertedContent();
      }
      return;
    }

    if (typeof value !== 'string' || !/[$!=]\{[^}]+\}/.test(value)) return;

    let hasPlaceholderNodes = false;
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'placeholderNode') hasPlaceholderNodes = true;
    });

    if (!hasPlaceholderNodes) {
      applyConvertedContent();
    }
  }, [value, editor, isSourceMode, availablePlaceholders, stripHtml, markdownMode]);


  // Handle source mode toggle
  const handleSourceToggle = useCallback(() => {
    if (isSourceMode) {
      // Switching from source to WYSIWYG
      editor?.commands.setContent(sourceContent, false);
      setIsSourceMode(false);
    } else {
      // Switching from WYSIWYG to source
      setSourceContent(editor?.getHTML() || '');
      setIsSourceMode(true);
    }
  }, [isSourceMode, sourceContent, editor]);

  // Handle source content change
  const handleSourceChange = useCallback((e) => {
    setSourceContent(e.target.value);
  }, []);

  // Handle image upload from toolbar
  const handleImageUpload = useCallback(async (file) => {
    if (!editor || !projectId) return;

    const url = await uploadImage(file, projectId);
    if (url) {
      editor.chain().focus().setImage({ src: url, alt: '' }).run();
    }
  }, [editor, projectId]);

  if (!editor) {
    return <div className="tiptap-loading">Loading editor...</div>;
  }

  const isOverLimit = characterLimit && charCount > characterLimit;
  
  // Map variants to CSS classes
  const variantClasses = {
    'document': 'tiptap-document-variant',
    'page': 'tiptap-page-variant',
    'minimal': 'tiptap-minimal',
    'outlined': 'tiptap-outlined',
  };
  
  const containerClasses = [
    'tiptap-editor-container',
    className,
    variantClasses[variant] || '',
    error ? 'error' : '',
    isOverLimit ? 'error' : '',
  ].filter(Boolean).join(' ');

  // Don't apply minHeight for document, page, or minimal variants - CSS handles it
  const containerStyle = (variant === 'document' || variant === 'page' || variant === 'minimal') ? {} : { minHeight };

  return (
    <div className={variant === 'page' ? 'tiptap-page-root' : undefined}>
      <div 
        className={containerClasses}
        ref={editorContainerRef}
        style={containerStyle}
      >
        {showToolbar && (
          <div className={variant === 'page' ? 'tiptap-page-toolbar' : undefined}>
            <EditorToolbar
              editor={editor}
              onImageUpload={handleImageUpload}
              onSourceToggle={handleSourceToggle}
              isSourceMode={isSourceMode}
            />
          </div>
        )}
        
        <div className="tiptap-editor-content">
          {isSourceMode ? (
            <textarea
              className="tiptap-source-editor"
              value={sourceContent}
              onChange={handleSourceChange}
              spellCheck={false}
            />
          ) : (
            <EditorContent editor={editor} />
          )}
        </div>
      </div>
      
      {/* Error/helper text */}
      {error && helperText && (
        <p style={{ color: '#B00020', margin: '5px 0 0', fontSize: '12px' }}>{helperText}</p>
      )}
      
      {/* Character limit warning */}
      {isOverLimit && (
        <Alert
          severity="error"
          icon={<ErrorIcon sx={{ color: '#B00020' }} />}
          sx={{
            backgroundColor: 'rgba(176, 0, 32, 0.15)',
            marginTop: '16px',
            width: 'fit-content',
            color: 'text.primary',
            fontWeight: '400',
            border: '1px solid rgba(176, 0, 32, 1)',
          }}
        >
          You have reached the character limit for this editor
        </Alert>
      )}
    </div>
  );
});

TipTapEditor.displayName = 'TipTapEditor';

export default TipTapEditor;
